import React, { useState, useCallback, useEffect } from 'react';
import { orderApi } from '../../lib/orderApi';
import { redButton } from '../PageShell';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const cfgInput = { background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.25)', color: '#F5F5F0', padding: '9px 11px', fontSize: '13px', outline: 'none', width: '100%' };
const smallInput = { ...cfgInput, padding: '7px 9px', fontSize: '12px' };
const labelStyle = { fontSize: '10px', color: '#9A7522', letterSpacing: '1px', textTransform: 'uppercase', display: 'block', marginBottom: '3px' };

const buildImageUrl = (img) => (!img ? '' : img.startsWith('http') ? img : `${BACKEND}${img}`);

// Resize image in-browser to keep upload small (bypasses proxy body limits) and DB light
const resizeImage = (file, maxW = 1400, quality = 0.82) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('read_error'));
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = () => reject(new Error('image_error'));
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

const emptyForm = { alt_es: '', alt_en: '', tag_es: '', tag_en: '', title_es: '', title_en: '', subtitle_es: '', subtitle_en: '', image_data: '' };

const HOURS_DAYS = [
  { key: 'monday', label: 'Lunes' },
  { key: 'tuesday', label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday', label: 'Jueves' },
  { key: 'friday', label: 'Viernes' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
];

const AdminTrending = ({ isOpen }) => {
  const [items, setItems] = useState([]);
  const [edits, setEdits] = useState({}); // id -> {tag_es, tag_en, title_es, ...}
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState('');
  const [form, setForm] = useState(emptyForm);

  // Horario del sitio web (sección "Ubicación & Horarios")
  const [hours, setHours] = useState(null);
  const [hoursMsg, setHoursMsg] = useState('');
  const [hoursBusy, setHoursBusy] = useState(false);

  const seedEdits = (list) => {
    const e = {};
    list.forEach((it) => {
      e[it.id] = {
        alt_es: it.alt?.es || '', alt_en: it.alt?.en || '',
        tag_es: it.tag?.es || '', tag_en: it.tag?.en || '',
        title_es: it.title?.es || '', title_en: it.title?.en || '',
        subtitle_es: it.subtitle?.es || '', subtitle_en: it.subtitle?.en || '',
      };
    });
    setEdits(e);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await orderApi.get('/admin/trending');
      setItems(data.items);
      seedEdits(data.items);
    } catch {
      setMsg('Error al cargar imágenes de trending');
    }
  }, []);

  const loadHours = useCallback(async () => {
    try {
      const { data } = await orderApi.get('/admin/hours');
      setHours(data.hours);
    } catch {
      setHoursMsg('Error al cargar el horario.');
    }
  }, []);

  useEffect(() => {
    if (isOpen) { load(); loadHours(); }
  }, [isOpen, load, loadHours]);

  const setHourField = (day, value) => setHours((p) => ({ ...p, [day]: value }));

  const saveHours = async () => {
    setHoursBusy(true);
    setHoursMsg('');
    try {
      const { data } = await orderApi.put('/admin/hours', hours);
      setHours(data.hours);
      setHoursMsg('✓ Horario actualizado en el sitio web.');
    } catch (err) {
      setHoursMsg('Error: ' + (err.response?.data?.detail || 'no se pudo guardar'));
    } finally {
      setHoursBusy(false);
    }
  };

  const setEditField = (id, field, value) => setEdits((p) => ({ ...p, [id]: { ...p[id], [field]: value } }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMsg('Selecciona un archivo de imagen.');
      return;
    }
    setMsg('');
    try {
      const dataUrl = await resizeImage(file);
      setForm((f) => ({ ...f, image_data: dataUrl }));
      setPreview(dataUrl);
    } catch {
      setMsg('No se pudo procesar la imagen.');
    }
  };

  const addImage = async () => {
    if (!form.image_data) {
      setMsg('Selecciona una imagen para subir.');
      return;
    }
    setBusy(true);
    setMsg('');
    try {
      await orderApi.post('/admin/trending', form);
      setForm(emptyForm);
      setPreview('');
      setMsg('✓ Imagen agregada al trending.');
      load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.detail || 'no se pudo subir'));
    } finally {
      setBusy(false);
    }
  };

  const saveText = async (id) => {
    setMsg('');
    try {
      await orderApi.patch(`/admin/trending/${id}`, edits[id]);
      setMsg('✓ Texto guardado.');
      load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.detail || ''));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta imagen del trending?')) return;
    setMsg('');
    try {
      await orderApi.delete(`/admin/trending/${id}`);
      setMsg('✓ Imagen eliminada.');
      load();
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.detail || ''));
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const reordered = [...items];
    const [it] = reordered.splice(index, 1);
    reordered.splice(target, 0, it);
    setItems(reordered);
    try {
      await orderApi.post('/admin/trending/reorder', { ids: reordered.map((x) => x.id) });
    } catch {
      setMsg('Error al reordenar.');
      load();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="mt-5 p-5" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.3)' }} data-testid="trending-panel">
      <h3 className="font-display" style={{ fontSize: '18px', fontWeight: 700, color: '#F5F5F0' }}>Imágenes de Trending</h3>
      <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }}>
        Sube, ordena o elimina las imágenes de la sección &quot;Lo que está trending&quot;. Añade etiqueta, título y subtítulo para mostrarlos como texto sobre la imagen.
      </p>
      {msg && (
        <p className="font-body mt-2" style={{ fontSize: '12px', color: msg.startsWith('✓') ? '#7FB77E' : '#E0736F' }} data-testid="trending-msg">
          {msg}
        </p>
      )}

      {/* Horario del sitio web (sección "Ubicación & Horarios") */}
      <div className="mt-6 p-4" style={{ background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.25)' }} data-testid="hours-panel">
        <p className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Horario del sitio web</p>
        <p className="font-body mt-1" style={{ fontSize: '12px', color: '#C2C1B6' }}>
          Edita el horario que se muestra en la sección &quot;Ubicación &amp; Horarios&quot; de la página. Escribe &quot;Cerrado&quot; para marcar un día como cerrado.
        </p>
        {!hours ? (
          <p className="font-body mt-3" style={{ fontSize: '13px', color: '#C2C1B6' }}>Cargando horario...</p>
        ) : (
          <>
            <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
              {HOURS_DAYS.map(({ key, label }) => (
                <div key={key}>
                  <label style={labelStyle}>{label}</label>
                  <input
                    value={hours[key] || ''}
                    onChange={(e) => setHourField(key, e.target.value)}
                    placeholder="Ej: 12 PM – 9:30 PM"
                    style={smallInput}
                    data-testid={`hours-input-${key}`}
                  />
                </div>
              ))}
            </div>
            <button
              onClick={saveHours}
              disabled={hoursBusy}
              className="font-body mt-3"
              style={{ fontSize: '12px', color: '#0A0A0A', background: '#C9A84C', border: 'none', padding: '7px 16px', cursor: hoursBusy ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: hoursBusy ? 0.6 : 1 }}
              data-testid="hours-save-btn"
            >
              {hoursBusy ? 'Guardando…' : 'Guardar horario'}
            </button>
            {hoursMsg && (
              <p className="font-body mt-2" style={{ fontSize: '12px', color: hoursMsg.startsWith('✓') ? '#7FB77E' : '#E0736F' }} data-testid="hours-msg">
                {hoursMsg}
              </p>
            )}
          </>
        )}
      </div>

      {/* Current images */}
      <div className="mt-4 space-y-4">
        {items.length === 0 && (
          <p className="font-body" style={{ fontSize: '13px', color: '#C2C1B6' }}>No hay imágenes todavía.</p>
        )}
        {items.map((it, i) => {
          const ed = edits[it.id] || {};
          return (
            <div key={it.id} className="p-3" style={{ background: '#0A0A0A', border: '1px solid rgba(201,168,76,0.14)' }} data-testid={`trending-item-${it.id}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <img
                  src={buildImageUrl(it.image)}
                  alt={it.alt?.es || 'trending'}
                  style={{ width: '90px', height: '60px', objectFit: 'cover', border: '1px solid rgba(201,168,76,0.2)' }}
                />
                <span className="font-body" style={{ flex: '1 1 160px', fontSize: '13px', color: '#F5F5F0' }}>
                  {it.title?.es || it.alt?.es || <span style={{ color: '#9A7522' }}>Sin título</span>}
                  <span style={{ color: '#9A7522', fontSize: '11px' }}> · #{i + 1}</span>
                </span>
                <button
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="font-body"
                  style={{ fontSize: '14px', color: i === 0 ? '#5a5a52' : '#C9A84C', background: 'none', border: '1px solid rgba(201,168,76,0.3)', padding: '5px 10px', cursor: i === 0 ? 'not-allowed' : 'pointer' }}
                  data-testid={`trending-up-${it.id}`}
                  aria-label="Subir"
                >
                  ↑
                </button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={i === items.length - 1}
                  className="font-body"
                  style={{ fontSize: '14px', color: i === items.length - 1 ? '#5a5a52' : '#C9A84C', background: 'none', border: '1px solid rgba(201,168,76,0.3)', padding: '5px 10px', cursor: i === items.length - 1 ? 'not-allowed' : 'pointer' }}
                  data-testid={`trending-down-${it.id}`}
                  aria-label="Bajar"
                >
                  ↓
                </button>
                <button
                  onClick={() => remove(it.id)}
                  className="font-body"
                  style={{ fontSize: '12px', color: '#E0736F', background: 'none', border: '1px solid rgba(224,115,111,0.4)', padding: '6px 12px', cursor: 'pointer' }}
                  data-testid={`trending-delete-${it.id}`}
                >
                  Eliminar
                </button>
              </div>

              {/* Text overlay fields */}
              <div className="mt-3 grid gap-2" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                <div>
                  <label style={labelStyle}>Etiqueta (ES)</label>
                  <input value={ed.tag_es || ''} onChange={(e) => setEditField(it.id, 'tag_es', e.target.value)} placeholder="Ej: Nuevo" style={smallInput} data-testid={`trending-tag-es-${it.id}`} />
                </div>
                <div>
                  <label style={labelStyle}>Tag (EN)</label>
                  <input value={ed.tag_en || ''} onChange={(e) => setEditField(it.id, 'tag_en', e.target.value)} placeholder="Ex: New" style={smallInput} data-testid={`trending-tag-en-${it.id}`} />
                </div>
                <div>
                  <label style={labelStyle}>Título (ES)</label>
                  <input value={ed.title_es || ''} onChange={(e) => setEditField(it.id, 'title_es', e.target.value)} placeholder="Ej: Takoyakis de Pulpo" style={smallInput} data-testid={`trending-title-es-${it.id}`} />
                </div>
                <div>
                  <label style={labelStyle}>Title (EN)</label>
                  <input value={ed.title_en || ''} onChange={(e) => setEditField(it.id, 'title_en', e.target.value)} placeholder="Ex: Octopus Takoyakis" style={smallInput} data-testid={`trending-title-en-${it.id}`} />
                </div>
                <div>
                  <label style={labelStyle}>Subtítulo (ES)</label>
                  <input value={ed.subtitle_es || ''} onChange={(e) => setEditField(it.id, 'subtitle_es', e.target.value)} placeholder="Ej: Tu próxima obsesión." style={smallInput} data-testid={`trending-subtitle-es-${it.id}`} />
                </div>
                <div>
                  <label style={labelStyle}>Subtitle (EN)</label>
                  <input value={ed.subtitle_en || ''} onChange={(e) => setEditField(it.id, 'subtitle_en', e.target.value)} placeholder="Ex: Your next obsession." style={smallInput} data-testid={`trending-subtitle-en-${it.id}`} />
                </div>
              </div>
              <button
                onClick={() => saveText(it.id)}
                className="font-body mt-2"
                style={{ fontSize: '12px', color: '#0A0A0A', background: '#C9A84C', border: 'none', padding: '7px 16px', cursor: 'pointer', fontWeight: 600 }}
                data-testid={`trending-save-text-${it.id}`}
              >
                Guardar texto
              </button>
            </div>
          );
        })}
      </div>

      {/* Upload new */}
      <div className="mt-6 p-4" style={{ background: '#0A0A0A', border: '1px dashed rgba(201,168,76,0.3)' }}>
        <p className="font-body uppercase" style={{ fontSize: '10px', letterSpacing: '2px', color: '#C9A84C' }}>Subir nueva imagen</p>
        <div className="mt-3 flex flex-wrap gap-3 items-start">
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            className="font-body"
            style={{ fontSize: '13px', color: '#C2C1B6' }}
            data-testid="trending-file-input"
          />
          {preview && (
            <img src={preview} alt="preview" style={{ width: '120px', height: '80px', objectFit: 'cover', border: '1px solid rgba(201,168,76,0.3)' }} data-testid="trending-preview" />
          )}
        </div>
        <div className="grid gap-2 mt-3" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <input placeholder="Etiqueta (ES) — opcional" value={form.tag_es} onChange={(e) => setForm({ ...form, tag_es: e.target.value })} style={smallInput} data-testid="trending-new-tag-es" />
          <input placeholder="Tag (EN) — optional" value={form.tag_en} onChange={(e) => setForm({ ...form, tag_en: e.target.value })} style={smallInput} data-testid="trending-new-tag-en" />
          <input placeholder="Título (ES) — opcional" value={form.title_es} onChange={(e) => setForm({ ...form, title_es: e.target.value })} style={smallInput} data-testid="trending-new-title-es" />
          <input placeholder="Title (EN) — optional" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} style={smallInput} data-testid="trending-new-title-en" />
          <input placeholder="Subtítulo (ES) — opcional" value={form.subtitle_es} onChange={(e) => setForm({ ...form, subtitle_es: e.target.value })} style={smallInput} data-testid="trending-new-subtitle-es" />
          <input placeholder="Subtitle (EN) — optional" value={form.subtitle_en} onChange={(e) => setForm({ ...form, subtitle_en: e.target.value })} style={smallInput} data-testid="trending-new-subtitle-en" />
          <input placeholder="Descripción/alt (ES) — opcional" value={form.alt_es} onChange={(e) => setForm({ ...form, alt_es: e.target.value })} style={smallInput} data-testid="trending-alt-es" />
          <input placeholder="Description/alt (EN) — optional" value={form.alt_en} onChange={(e) => setForm({ ...form, alt_en: e.target.value })} style={smallInput} data-testid="trending-alt-en" />
        </div>
        <button
          onClick={addImage}
          disabled={busy || !form.image_data}
          style={{ ...redButton, marginTop: '12px', opacity: busy || !form.image_data ? 0.6 : 1 }}
          data-testid="trending-add-btn"
        >
          {busy ? 'Subiendo…' : 'Agregar al trending'}
        </button>
      </div>
    </div>
  );
};

export default AdminTrending;
